import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/dashboard/admin-sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/theme-toggle";
import { Plus, Loader2, Trash2, Edit2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, API_BASE_URL } from "@/lib/queryClient";
import { formatPlatformCurrencyExact } from "@/lib/currency";
import { Switch } from "@/components/ui/switch";

const investorReturnSchema = z.object({
  portfolioId: z.string().min(1, "Portfolio ID is required"),
  investorId: z.string().min(1, "Investor ID is required"),
  month: z.string().min(1, "Month is required"),
  year: z.number().int().min(2020),
  returnsPercentage: z.string().min(1, "Returns percentage is required"),
  returnsAmount: z.string().min(1, "Returns amount is required"),
  isWithdrawn: z.boolean().default(false),
});

type InvestorReturnFormData = z.infer<typeof investorReturnSchema>;

// Month options
const months = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

// Provide some recent years
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

export default function AdminInvestorReturnsPage() {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all investor returns
  const { data: returns, isLoading: loadingReturns } = useQuery({
    queryKey: ["/api/admin/investor-returns"],
  });

  // Fetch all portfolios to get investor mappings easily
  const { data: portfolios } = useQuery({
    queryKey: ["/api/admin/portfolios"], // Assuming this exists or falls back
    queryFn: async () => {
       const res = await fetch(`${API_BASE_URL}/api/admin/portfolios`, { credentials: "include" });
       if (!res.ok) return []; // Graceful fallback
       return res.json();
    }
  });
  
  // Optionally fetch investors for names
  const { data: investors } = useQuery({
    queryKey: ["/api/admin/investors"]
  });

  const getInvestorDisplay = (investorId: string) => {
    if (!investors) return { name: investorId, clientId: "—" };
    const inv = (investors as any[]).find((i: any) => (i.id || i._id) === investorId);
    return {
      name: inv ? `${inv.firstName || ""} ${inv.lastName || ""}`.trim() || "Unknown" : "Unknown",
      clientId: inv?.clientId || "—"
    };
  };

  const form = useForm<InvestorReturnFormData>({
    resolver: zodResolver(investorReturnSchema),
    defaultValues: {
      portfolioId: "",
      investorId: "",
      month: months[new Date().getMonth()],
      year: currentYear,
      returnsPercentage: "",
      returnsAmount: "",
      isWithdrawn: false,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: InvestorReturnFormData) => {
      const response = await apiRequest("POST", "/api/admin/investor-returns", data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Return Record Created",
        description: "The investor monthly return has been successfully added.",
      });
      setShowAddDialog(false);
      form.reset();
      queryClient.invalidateQueries({ queryKey: ["/api/admin/investor-returns"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/portfolios"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Creation Failed",
        description: error.message || "Failed to add return. Please check your inputs.",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("DELETE", `/api/admin/investor-returns/${id}`);
      if (!response.ok) {
        throw new Error("Failed to delete record");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Record Deleted",
        description: "The return record has been removed.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/investor-returns"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/portfolios"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Delete Failed",
        description: error.message || "Failed to delete return record.",
        variant: "destructive",
      });
    },
  });

  const handleInvestorChange = async (investorId: string) => {
    form.setValue("investorId", investorId);

    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/portfolios/${investorId}`, {
        credentials: "include",
      });
      if (!res.ok) {
        form.setValue("portfolioId", "");
        return;
      }

      const portfolios = await res.json();
      const portfolio = portfolios?.[0];
      if (portfolio) {
        form.setValue("portfolioId", portfolio.id || portfolio._id);
      } else {
        form.setValue("portfolioId", "");
      }
    } catch {
      form.setValue("portfolioId", "");
    }
  };

  const onSubmit = (data: InvestorReturnFormData) => {
    if (!data.portfolioId) {
      toast({
        title: "Portfolio not found",
        description: "This investor does not have a portfolio yet. Create one before adding returns.",
        variant: "destructive",
      });
      return;
    }

    createMutation.mutate(data);
  };

  const handleCloseDialog = () => {
    setShowAddDialog(false);
    form.reset();
  };

  const sidebarStyle = {
    "--sidebar-width": "17rem",
    "--sidebar-width-icon": "3.5rem",
  };

  return (
    <SidebarProvider style={sidebarStyle as React.CSSProperties}>
      <div className="flex h-screen w-full overflow-hidden">
        <AdminSidebar />
        <div className="flex flex-col flex-1 h-full overflow-hidden">
          <header className="flex items-center justify-between gap-4 h-16 px-6 border-b bg-background/95 backdrop-blur shrink-0">
            <div className="flex items-center gap-4">
              <SidebarTrigger />
              <div>
                <h1 className="text-lg font-semibold">Investor Returns Inputs</h1>
                <p className="text-sm text-muted-foreground">Manage monthly returns data for investors</p>
              </div>
            </div>
            <ThemeToggle />
          </header>

          <main className="flex-1 p-6 space-y-6 overflow-y-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="relative max-w-sm w-full">
                {/* Search if needed later */}
              </div>
              <Button className="gap-2" onClick={() => setShowAddDialog(true)}>
                <Plus className="w-4 h-4" />
                Add Monthly Return
              </Button>
            </div>

            <Card className="bg-card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">
                  Returns Records
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Investor</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Period</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Returns (%)</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Returns (Amt)</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Withdrawn</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingReturns ? (
                        <tr>
                          <td colSpan={6} className="text-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                          </td>
                        </tr>
                      ) : returns && returns.length > 0 ? (
                        returns.map((ret: any) => (
                          <tr key={ret.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                            <td className="py-4 px-4 text-sm font-medium">
                               {getInvestorDisplay(ret.investorId).name}
                               <p className="text-xs text-muted-foreground font-normal">ID: {getInvestorDisplay(ret.investorId).clientId}</p>
                            </td>
                            <td className="py-4 px-4 text-sm">
                              {ret.month} {ret.year}
                            </td>
                            <td className="py-4 px-4 text-right text-sm">
                              <span className="text-green-600 dark:text-green-400 font-medium">
                                 +{ret.returnsPercentage}%
                              </span>
                            </td>
                            <td className="py-4 px-4 text-right text-sm">
                              {formatPlatformCurrencyExact(Number(ret.returnsAmount))}
                            </td>
                            <td className="py-4 px-4 text-center">
                              {ret.isWithdrawn ? (
                                <span className="text-xs font-medium text-destructive px-2 py-1 rounded-full bg-destructive/10">Yes</span>
                              ) : (
                                <span className="text-xs font-medium text-muted-foreground px-2 py-1 rounded-full bg-muted">No</span>
                              )}
                            </td>
                            <td className="py-4 px-4 text-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => {
                                  if (confirm("Are you sure you want to delete this record?")) {
                                    deleteMutation.mutate(ret._id || ret.id);
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="text-center py-8 text-muted-foreground text-sm">
                            No monthly return records found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </main>
        </div>
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Monthly Return</DialogTitle>
            <DialogDescription>
              Record a new monthly return entry for a specific investor's portfolio.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="investorId">Select Investor</Label>
              <Select onValueChange={handleInvestorChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Investor" />
                </SelectTrigger>
                <SelectContent>
                  {investors?.map((inv: any) => (
                    <SelectItem key={inv.id || inv._id} value={inv.id || inv._id}>
                      {inv.firstName} {inv.lastName} ({inv.clientId || "No ID"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.investorId && (
                <p className="text-sm text-destructive">{form.formState.errors.investorId.message}</p>
              )}
              {form.watch("portfolioId") ? (
                <p className="text-xs text-muted-foreground">
                  Linked portfolio ready for this investor.
                </p>
              ) : form.watch("investorId") ? (
                <p className="text-xs text-destructive">
                  No portfolio found for this investor.
                </p>
              ) : null}
              <input type="hidden" {...form.register("portfolioId")} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="month">Month</Label>
                <Select onValueChange={(val) => form.setValue("month", val)} defaultValue={form.getValues("month")}>
                  <SelectTrigger>
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="year">Year</Label>
                <Select
                  onValueChange={(val) => form.setValue("year", parseInt(val))}
                  defaultValue={form.getValues("year").toString()}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="returnsPercentage">Returns (%)</Label>
                <Input
                  id="returnsPercentage"
                  placeholder="2.5"
                  {...form.register("returnsPercentage")}
                />
                {form.formState.errors.returnsPercentage && (
                  <p className="text-sm text-destructive">{form.formState.errors.returnsPercentage.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="returnsAmount">Returns (INR)</Label>
                <Input
                  id="returnsAmount"
                  type="number"
                  placeholder="250000"
                  {...form.register("returnsAmount")}
                />
                {form.formState.errors.returnsAmount && (
                  <p className="text-sm text-destructive">{form.formState.errors.returnsAmount.message}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2 pt-2">
               <Switch
                 id="isWithdrawn"
                 checked={form.watch("isWithdrawn")}
                 onCheckedChange={(checked) => form.setValue("isWithdrawn", checked)}
               />
               <Label htmlFor="isWithdrawn">Returns Withdrawn?</Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                ) : (
                  "Save Entry"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
}
